import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  /**
   * Controller for all event listeners in this component.
   * @type {AbortController}
   */
  #controller = new AbortController();

  /**
   * Last media-gallery received from a variant update.
   * @type {MediaGallery | null}
   */
  #pendingMediaGallery = null;

  /**
   * requestAnimationFrame id for scheduled gallery replacement.
   * @type {number | null}
   */
  #variantUpdateFrame = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();

    if (this.#variantUpdateFrame !== null) {
      window.cancelAnimationFrame(this.#variantUpdateFrame);
      this.#variantUpdateFrame = null;
    }

    this.#pendingMediaGallery = null;
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   * The replacement is scheduled in requestAnimationFrame so variant interactions do not do
   * heavy DOM work directly inside the event handler.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    if (!source) return;

    const newMediaGallery = source.querySelector?.('media-gallery');
    if (!newMediaGallery) return;

    if (this.#hasSamePrimaryMedia(newMediaGallery)) return;

    this.#pendingMediaGallery = newMediaGallery;

    if (this.#variantUpdateFrame !== null) return;

    this.#variantUpdateFrame = window.requestAnimationFrame(() => {
      this.#variantUpdateFrame = null;
      this.#replaceWithPendingGallery();
    });
  };

  /**
   * Replaces this gallery with the most recent pending gallery.
   */
  #replaceWithPendingGallery() {
    const newMediaGallery = this.#pendingMediaGallery;
    this.#pendingMediaGallery = null;

    if (!newMediaGallery || !this.isConnected) return;

    this.replaceWith(newMediaGallery);
  }

  /**
   * Avoids replacing the whole gallery when the variant update did not actually change
   * the primary visible media.
   *
   * @param {Element} newMediaGallery
   * @returns {boolean}
   */
  #hasSamePrimaryMedia(newMediaGallery) {
    const currentSignature = this.#getPrimaryMediaSignature(this);
    const nextSignature = this.#getPrimaryMediaSignature(newMediaGallery);

    return Boolean(currentSignature && nextSignature && currentSignature === nextSignature);
  }

  /**
   * Creates a lightweight signature from the first visible media.
   *
   * @param {Element} gallery
   * @returns {string}
   */
  #getPrimaryMediaSignature(gallery) {
    const mainMedia = gallery.querySelector('.product-media-container:not(.dialog-zoomed-gallery .product-media-container)');
    if (!mainMedia) return '';

    const image = mainMedia.querySelector('img');

    if (image) {
      return [
        gallery.getAttribute('data-presentation') || '',
        mainMedia.className || '',
        mainMedia.getAttribute('style') || '',
        image.getAttribute('src') || '',
        image.getAttribute('srcset') || '',
        image.getAttribute('sizes') || '',
      ].join('|');
    }

    const model = mainMedia.querySelector('model-viewer, product-model');
    if (model) {
      return [
        gallery.getAttribute('data-presentation') || '',
        mainMedia.className || '',
        mainMedia.getAttribute('style') || '',
        model.getAttribute('src') || '',
        model.getAttribute('data-media-id') || '',
      ].join('|');
    }

    const video = mainMedia.querySelector('video, iframe, deferred-media');
    if (video) {
      return [
        gallery.getAttribute('data-presentation') || '',
        mainMedia.className || '',
        mainMedia.getAttribute('style') || '',
        video.getAttribute('src') || '',
        video.getAttribute('data-media-id') || '',
      ].join('|');
    }

    return [
      gallery.getAttribute('data-presentation') || '',
      mainMedia.className || '',
      mainMedia.getAttribute('style') || '',
      mainMedia.id || '',
    ].join('|');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = (event) => {
    const index = event.detail?.index;

    if (!Number.isFinite(index)) return;

    // The slideshow custom element may still be upgrading on an early tap.
    this.slideshow?.select?.(index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
